/**
 * Similarity Engine - Computes similarity between opportunities and historical awards
 *
 * Scoring is deterministic and based on weighted factors:
 * - NAICS code match (exact or prefix)
 * - PSC code match
 * - Agency match
 * - Text similarity (description/keywords)
 */

/**
 * Weight configuration for similarity factors
 */
const WEIGHTS = {
  naics_exact: 0.25,      // Exact NAICS match
  naics_prefix: 0.15,     // NAICS prefix match (first 4 digits)
  psc_exact: 0.15,        // Exact PSC match
  psc_prefix: 0.08,       // PSC prefix match (first 2 chars)
  agency: 0.12,           // Same awarding agency
  sub_agency: 0.05,       // Same sub-agency
  text_similarity: 0.20,  // Description text similarity
};

/**
 * Tokenize text into normalized words
 * @param {string} text - Input text
 * @returns {string[]} Array of lowercase tokens
 */
function tokenize(text) {
  if (!text || typeof text !== "string") return [];

  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 2)
    .filter((word) => !STOP_WORDS.has(word));
}

/**
 * Common stop words to filter out
 */
const STOP_WORDS = new Set([
  "the", "and", "for", "are", "but", "not", "you", "all", "can", "had",
  "her", "was", "one", "our", "out", "has", "have", "been", "were", "they",
  "this", "that", "with", "from", "will", "would", "there", "their", "what",
  "about", "which", "when", "make", "like", "time", "just", "know", "take",
  "come", "could", "than", "only", "other", "into", "year", "your", "good",
  "some", "them", "see", "way", "look", "first", "also", "new", "because",
  "day", "more", "use", "man", "find", "here", "thing", "give", "many",
  "well", "services", "service", "contract", "contractor", "federal",
  "government", "provide", "providing", "includes", "including", "support",
  "shall", "must", "may", "required", "requirements", "work", "perform",
]);

/**
 * Build term frequency map from tokens
 * @param {string[]} tokens - Array of tokens
 * @returns {Map<string, number>} Term frequency map
 */
function buildTermFrequency(tokens) {
  const freq = new Map();
  for (const token of tokens) {
    freq.set(token, (freq.get(token) || 0) + 1);
  }
  return freq;
}

/**
 * Calculate cosine similarity between two term frequency maps
 * @param {Map<string, number>} tf1 - First term frequency map
 * @param {Map<string, number>} tf2 - Second term frequency map
 * @returns {number} Similarity score between 0 and 1
 */
function cosineSimilarity(tf1, tf2) {
  if (tf1.size === 0 || tf2.size === 0) return 0;

  let dotProduct = 0;
  let magnitude1 = 0;
  let magnitude2 = 0;

  // Calculate dot product and magnitude of tf1
  for (const [term, count] of tf1) {
    magnitude1 += count * count;
    if (tf2.has(term)) {
      dotProduct += count * tf2.get(term);
    }
  }

  // Calculate magnitude of tf2
  for (const count of tf2.values()) {
    magnitude2 += count * count;
  }

  magnitude1 = Math.sqrt(magnitude1);
  magnitude2 = Math.sqrt(magnitude2);

  if (magnitude1 === 0 || magnitude2 === 0) return 0;

  return dotProduct / (magnitude1 * magnitude2);
}

/**
 * Calculate Jaccard similarity between two token sets
 * @param {Set<string>} set1 - First token set
 * @param {Set<string>} set2 - Second token set
 * @returns {number} Jaccard similarity between 0 and 1
 */
function jaccardSimilarity(set1, set2) {
  if (set1.size === 0 || set2.size === 0) return 0;

  let intersection = 0;
  for (const item of set1) {
    if (set2.has(item)) intersection++;
  }

  const union = set1.size + set2.size - intersection;
  return union > 0 ? intersection / union : 0;
}

/**
 * Calculate text similarity between two descriptions
 * Uses a combination of cosine and Jaccard similarity
 *
 * @param {string} text1 - First text
 * @param {string} text2 - Second text
 * @returns {number} Similarity score between 0 and 1
 */
function calculateTextSimilarity(text1, text2) {
  const tokens1 = tokenize(text1);
  const tokens2 = tokenize(text2);

  if (tokens1.length === 0 || tokens2.length === 0) return 0;

  // Cosine similarity (term frequency based)
  const tf1 = buildTermFrequency(tokens1);
  const tf2 = buildTermFrequency(tokens2);
  const cosine = cosineSimilarity(tf1, tf2);

  // Jaccard similarity (set-based)
  const set1 = new Set(tokens1);
  const set2 = new Set(tokens2);
  const jaccard = jaccardSimilarity(set1, set2);

  // Combined score (weighted average)
  return cosine * 0.6 + jaccard * 0.4;
}

/**
 * Check if NAICS codes match (exact or prefix)
 * @param {string} naics1 - First NAICS code
 * @param {string} naics2 - Second NAICS code
 * @returns {{exact: boolean, prefix: boolean}} Match results
 */
function matchNaics(naics1, naics2) {
  if (!naics1 || !naics2) return { exact: false, prefix: false };

  const n1 = naics1.toString().trim();
  const n2 = naics2.toString().trim();

  if (n1 === n2) return { exact: true, prefix: true };

  // Check 4-digit prefix match (industry group level)
  const prefix1 = n1.substring(0, 4);
  const prefix2 = n2.substring(0, 4);

  return { exact: false, prefix: prefix1 === prefix2 };
}

/**
 * Check if PSC codes match (exact or prefix)
 * @param {string} psc1 - First PSC code
 * @param {string} psc2 - Second PSC code
 * @returns {{exact: boolean, prefix: boolean}} Match results
 */
function matchPsc(psc1, psc2) {
  if (!psc1 || !psc2) return { exact: false, prefix: false };

  const p1 = psc1.toString().toUpperCase().trim();
  const p2 = psc2.toString().toUpperCase().trim();

  if (p1 === p2) return { exact: true, prefix: true };

  // Check 2-character prefix match
  const prefix1 = p1.substring(0, 2);
  const prefix2 = p2.substring(0, 2);

  return { exact: false, prefix: prefix1 === prefix2 };
}

/**
 * Normalize agency name for comparison
 * @param {string} agency - Agency name
 * @returns {string} Normalized agency name
 */
function normalizeAgency(agency) {
  if (!agency) return "";
  return agency.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Compute similarity score between an opportunity and a historical award
 *
 * @param {Object} opportunity - New opportunity/action
 * @param {Object} award - Historical award to compare against
 * @returns {number} Similarity score between 0 and 1
 */
export function computeSimilarityScore(opportunity, award) {
  let score = 0;

  // NAICS matching
  const naicsMatch = matchNaics(opportunity.naics_code, award.naics_code);
  if (naicsMatch.exact) {
    score += WEIGHTS.naics_exact;
  } else if (naicsMatch.prefix) {
    score += WEIGHTS.naics_prefix;
  }

  // PSC matching
  const pscMatch = matchPsc(opportunity.psc_code, award.psc_code);
  if (pscMatch.exact) {
    score += WEIGHTS.psc_exact;
  } else if (pscMatch.prefix) {
    score += WEIGHTS.psc_prefix;
  }

  // Agency matching
  const oppAgency = normalizeAgency(opportunity.agency_name);
  const awardAgency = normalizeAgency(award.agency_name);
  if (oppAgency && awardAgency && oppAgency === awardAgency) {
    score += WEIGHTS.agency;
  }

  // Sub-agency matching
  const oppSubAgency = normalizeAgency(opportunity.sub_agency_name);
  const awardSubAgency = normalizeAgency(award.sub_agency_name);
  if (oppSubAgency && awardSubAgency && oppSubAgency === awardSubAgency) {
    score += WEIGHTS.sub_agency;
  }

  // Text similarity (description)
  const textSim = calculateTextSimilarity(
    opportunity.description || "",
    award.description || ""
  );
  score += textSim * WEIGHTS.text_similarity;

  // Ensure score is between 0 and 1
  return Math.min(1, Math.max(0, score));
}

/**
 * Convert similarity score to band classification
 *
 * @param {number} score - Similarity score between 0 and 1
 * @returns {"high" | "medium" | "low" | null} Similarity band
 */
export function computeSimilarityBand(score) {
  if (score >= 0.7) return "high";
  if (score >= 0.4) return "medium";
  if (score > 0.1) return "low";
  return null;
}

/**
 * Find similar awards for an opportunity from a list of historical awards
 *
 * @param {Object} opportunity - The opportunity to find similar awards for
 * @param {Object[]} historicalAwards - Array of historical awards
 * @param {Object} options - Configuration options
 * @param {number} options.minScore - Minimum score to include (default 0.1)
 * @param {number} options.maxResults - Maximum results to return (default 10)
 * @returns {Object[]} Array of similar awards with scores
 */
export function findSimilarAwards(opportunity, historicalAwards, options = {}) {
  const { minScore = 0.1, maxResults = 10 } = options;

  const scored = historicalAwards
    .map((award) => ({
      award,
      score: computeSimilarityScore(opportunity, award),
    }))
    .filter((item) => item.score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults);

  return scored.map((item) => ({
    award_action_id: item.award.award_id || item.award.piid,
    piid: item.award.piid,
    similarity_score: Math.round(item.score * 100) / 100,
    similarity_band: computeSimilarityBand(item.score),
    award_value: item.award.current_award_value,
    award_date: item.award.effective_date,
    agency_name: item.award.agency_name,
    vendor_name: item.award.vendor_name,
    description: item.award.description?.substring(0, 200),
  }));
}

/**
 * Batch process multiple opportunities against historical awards
 *
 * @param {Object[]} opportunities - Array of opportunities
 * @param {Object[]} historicalAwards - Array of historical awards
 * @param {Object} options - Same options as findSimilarAwards
 * @returns {Map<string, Object[]>} Map of opportunity ID to similar awards
 */
export function batchFindSimilarAwards(opportunities, historicalAwards, options = {}) {
  const results = new Map();

  for (const opp of opportunities) {
    const oppId = opp.award_id || opp.piid || opp.id;
    const similar = findSimilarAwards(opp, historicalAwards, options);
    results.set(oppId, similar);
  }

  return results;
}

// Export utility functions for testing
export {
  tokenize,
  calculateTextSimilarity,
  matchNaics,
  matchPsc,
  WEIGHTS,
};
